import { ServiceStoreConfig } from "../types/ServiceStoreTypes";

/**
 * User Module Service Stores
 *
 * Note: the API exposes the singular `user` service path, and RootStore
 * already mounts a builtin `userStore` against it — a configured "users"
 * store here would point at a non-existent service (404). Only user-adjacent
 * services belong in this file.
 *
 * `user-permissions` is NOT configured here for the same reason, and it is not
 * a style preference — it was a crash. The library RootStore mounts a built-in
 * `userPermissionsStore` against that exact path, and `userPermissionsStore` is
 * a RESERVED key. A config here derives the same key, the service-store batches
 * mount synchronously (so the configured store registers FIRST), and the
 * built-in mount then hits the registry's duplicate path inside the RootStore
 * constructor. Consumers read the built-in store directly — see
 * `PageAccessStore.permissionsService` and `PermissionsStore.permissionsService`.
 */
export const userStores: ServiceStoreConfig[] = [
  // `roles` — the definition table behind the app's RBAC. Reads only; every
  // write goes through the raw Feathers service from RolesStore.
  //
  // `serviceView` is deliberately OFF. The reviewed roster in
  // views/utils/ServiceViewRouteGenerator.test.ts enables the generic CRUD grid
  // only for flat reference data and forbids "anything with business rules in
  // hooks or a real blast radius" — `roles` is both: its hooks refuse system
  // deletes, isSystem changes and system tier changes, and a role's `tier` is
  // what decides who may write anything. It gets the hand-built Roles view
  // (views/administration/Roles) instead, which surfaces those rules as UI.
  {
    name: "roles",
    options: {
      // No offline copy, and every find replaces the cache (#2654). A deleted
      // role is soft-deleted by the API and RolesStore re-reads after the
      // delete, but a merging find never drops a row the answer leaves out —
      // so the role you had just deleted stayed in the list, and with the old
      // offline copy it came back on every reload. Both readers (RolesStore,
      // UserManagementStore's picker) ask for the whole table.
      enableStorage: false,
      replaceOnFind: true,
      enableErrorNotifications: true,
      idField: "id",
    },
  },
  // Delegated user administration (views/administration/People). Reads only;
  // every write goes through the raw Feathers service from PeopleStore, so a
  // refusal reaches the dialog in the API's words. All three: no offline copy
  // (access information about other people never sits on a device), every
  // find replaces the cache (a revoked delegation or a person who left a
  // delegate's reach must leave the list), and a refused read THROWS
  // (`throwOnError`) so the page says it could not load instead of "nobody".
  //
  // `user-admin-delegations`: an administrator reads every delegation, anyone
  // else only their own — which is how PeopleAccessStore knows to offer People.
  {
    name: "user-admin-delegations",
    options: { idField: "id", enableStorage: false, replaceOnFind: true, throwOnError: true },
  },
  // The append-only record of delegated changes ({ total, data } envelope).
  {
    name: "user-admin-delegation-log",
    options: { idField: "id", enableStorage: false, replaceOnFind: true, throwOnError: true },
  },
  // A people administrator's own list of whom they may administer, each with
  // the actions the API's guard would accept.
  {
    name: "delegated-people",
    options: { idField: "id", enableStorage: false, replaceOnFind: true, throwOnError: true },
  },
];
